import { useEffect, useState } from 'react';
import * as Accordion from '@radix-ui/react-accordion';
import { Field, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';

import { icon_plus } from '@gn-audio/icons';
import {
  Button,
  ComponentWithContextProps,
  getContrastThemeClass,
  JabraIcon,
  resolveBackgroundStyle,
  useFallbackTranslation,
  withTheme,
} from '@gn-audio/jss-components';
import { tailwindConfig } from '@gn-audio/jss-components/tailwind-presets';

import { useSpotTracking } from 'utils/hooks/useSpotTracking';

type FAQSpotItem = {
  id: string;
  question: Field<string>;
  answer: Field<string>;
};

type FAQSpotRendering = {
  fields: {
    faqQuestions: FAQSpotItem[];
    title: Field<string>;
    background: Field<string>;
    trackingSpotName?: Field<string>;
  };
} & ComponentWithContextProps;

const FAQSpot = ({ fields }: FAQSpotRendering) => {
  const { t } = useFallbackTranslation();
  const spotRef = useSpotTracking(fields.trackingSpotName?.value);
  const handleShowMore = () => {
    setIsShowMoreButtonVisible(false);
  };

  const faqItems =
    fields.faqQuestions?.filter((faqQuestion) => Object.keys(faqQuestion).length > 0) || [];

  const limitedFaqs = faqItems.slice(0, 10);
  const initialState = limitedFaqs.length < faqItems.length;
  const [isShowMoreButtonVisible, setIsShowMoreButtonVisible] = useState(initialState);
  const [shouldForceMount, setShouldForceMount] = useState(true);

  useEffect(() => {
    setShouldForceMount(false);
  }, []);

  if (!faqItems.length) return null;

  const faqs = isShowMoreButtonVisible ? limitedFaqs : faqItems;

  const spotThemeClass = getContrastThemeClass(fields.background.value as string);
  const spotBackgroundStyle = resolveBackgroundStyle(fields.background.value as string);

  const backgroundValue = fields.background.value as string;

  const combinedClassName = backgroundValue
    ? spotThemeClass
    : `bg-theme text-theme ${spotThemeClass}`;

  return (
    <>
      <div
        ref={spotRef}
        data-wt-spot={fields.trackingSpotName?.value}
        className={combinedClassName}
        style={spotBackgroundStyle}
      >
        <div className="container flex flex-col">
          <header>
            <h2 className="heading-2 mb-12 text-center md:mb-24">{fields?.title?.value}</h2>
          </header>

          <Accordion.Root
            orientation="horizontal"
            type="single"
            collapsible
            className="w-full text-left"
          >
            {faqs.map((faq, index) => (
              <Accordion.Item
                className="flex w-full flex-col overflow-hidden border-b-[1px] border-solid py-[18px]"
                key={faq.id}
                value={`item-${index}`}
              >
                <section>
                  <Accordion.Trigger className="group flex w-full items-center justify-between">
                    <div className="text-left font-normal leading-[21.89px]">
                      {faq.question.value}
                    </div>
                    <JabraIcon
                      iconStyle={{ strokeWidth: 2 }}
                      className="h-5 min-h-5 w-5 min-w-5 group-data-[state=open]:rotate-45"
                      Icon={icon_plus}
                    />
                  </Accordion.Trigger>
                </section>
                <Accordion.Content
                  {...(shouldForceMount ? { forceMount: true } : {})}
                  className={classNames(
                    'collapsible-content overflow-hidden',
                    shouldForceMount && 'data-[state=closed]:h-0'
                  )}
                >
                  <p
                    className="pt-5 text-base font-light leading-6"
                    dangerouslySetInnerHTML={{ __html: faq.answer.value }}
                  ></p>
                </Accordion.Content>
              </Accordion.Item>
            ))}
          </Accordion.Root>
          {isShowMoreButtonVisible && (
            <section className="mt-12 flex">
              <Button
                aria-label={`${t('system.ShowMoreFAQ')}`}
                size="m"
                variant="secondary"
                onClick={handleShowMore}
              >
                {t('system.ShowMoreFAQ') || 'SHOW MORE FAQ'}
              </Button>
            </section>
          )}
        </div>
      </div>
    </>
  );
};

const ThemedFAQSpot = withTheme({
  defaultTheme: {
    darkTheme: false,
    backgroundColor: tailwindConfig.theme.colors.white,
  },
})(withDatasourceCheck()(FAQSpot));

const PlainFAQSpot = withDatasourceCheck()(FAQSpot);

export default function FAQSpotExport(props: FAQSpotRendering) {
  if (props.fields?.background?.value) {
    return <PlainFAQSpot {...props} />;
  }
  return <ThemedFAQSpot {...props} />;
}
